/**
 * Due grafici, fatti a mano.
 *
 * Non c'è una libreria perché non serve: una barra impilata è tre rettangoli
 * alti in proporzione, una ciambella è un cerchio con il tratto spezzato. Con
 * i colori del tema e niente da configurare, restano leggibili anche quando
 * il resto dell'interfaccia cambia — e un grafico che non torna con il conto
 * accanto è peggio di nessun grafico, quindi i dati arrivano già pronti.
 */

import type { ReactNode } from "react";
import { cx } from "./primitives.tsx";

export interface Segmento {
  nome: string;
  valore: number;
  /** Classe di sfondo del tema: `bg-ok`, `bg-accent`, `bg-line-strong`… */
  classe: string;
}

export interface Colonna {
  etichetta: string;
  segmenti: Segmento[];
  attiva?: boolean;
  onClick?: () => void;
  /**
   * Il totale vero della colonna, quando non è la somma delle fette.
   *
   * Serve dove una fetta manca di proposito: con un mandato, quello che va al
   * proprietario non è né utile né trattenuta né costo, e sommare le tre fette
   * darebbe una barra più corta del venduto senza dire perché. Dichiarando il
   * totale, la parte mancante resta visibile come spazio vuoto in cima — che è
   * il modo onesto di disegnare quello che non si sta mostrando.
   */
  totale?: number;
}

/**
 * Barre impilate mese per mese. Ogni colonna somma le sue fette; la scala
 * è comune, così un mese grosso si vede grosso.
 */
export function BarreImpilate({
  colonne,
  formatta,
  altezza = 190,
}: {
  colonne: Colonna[];
  formatta: (valore: number) => string;
  altezza?: number;
}) {
  const totali = colonne.map((c) =>
    Math.max(
      c.totale ?? 0,
      c.segmenti.reduce((s, x) => s + Math.max(0, x.valore), 0),
    ),
  );
  const massimo = Math.max(1, ...totali);
  const tacche = [1, 0.75, 0.5, 0.25];
  const legenda = new Map<string, string>();
  for (const c of colonne)
    for (const s of c.segmenti) legenda.set(s.nome, s.classe);

  return (
    <div>
      <div className="flex gap-3">
        {/* Asse: quattro tacche, etichette a destra della linea. */}
        <div
          className="relative w-14 shrink-0 text-[11px] tabular-nums text-ink-faint"
          style={{ height: altezza }}
        >
          {tacche.map((t) => (
            <span
              key={t}
              className="absolute right-0 -translate-y-1/2"
              style={{ top: `${(1 - t) * 100}%` }}
            >
              {formatta(massimo * t)}
            </span>
          ))}
          <span className="absolute bottom-0 right-0 translate-y-1/2">0</span>
        </div>

        <div className="relative flex-1" style={{ height: altezza }}>
          {tacche.map((t) => (
            <div
              key={t}
              className="absolute inset-x-0 border-t border-dashed border-line"
              style={{ top: `${(1 - t) * 100}%` }}
            />
          ))}
          <div className="absolute inset-x-0 bottom-0 border-t border-line-strong" />

          <div className="absolute inset-0 flex items-end justify-around gap-2 px-2">
            {colonne.map((c, i) => (
              <button
                key={c.etichetta}
                type="button"
                onClick={c.onClick}
                disabled={!c.onClick}
                title={`${c.etichetta}: ${formatta(totali[i]!)}`}
                className={cx(
                  "group flex h-full w-full max-w-[56px] flex-col justify-end rounded-t-[6px] transition-opacity duration-quick",
                  c.onClick && "cursor-pointer hover:opacity-90",
                  colonne.some((x) => x.attiva) && !c.attiva && "opacity-55",
                )}
              >
                <div
                  className="flex w-full flex-col-reverse overflow-hidden rounded-[6px]"
                  style={{ height: `${(totali[i]! / massimo) * 100}%` }}
                >
                  {c.segmenti.map((s) =>
                    s.valore > 0 ? (
                      <div
                        key={s.nome}
                        className={s.classe}
                        style={{
                          height: `${(s.valore / Math.max(1, totali[i]!)) * 100}%`,
                        }}
                      />
                    ) : null,
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="ml-14 mt-2 flex justify-around gap-2 px-2">
        {colonne.map((c) => (
          <span
            key={c.etichetta}
            className={cx(
              "w-full max-w-[56px] truncate text-center text-[11.5px] capitalize",
              c.attiva ? "font-bold text-ink" : "text-ink-soft",
            )}
          >
            {c.etichetta}
          </span>
        ))}
      </div>

      <Legenda
        voci={[...legenda.entries()].map(([nome, classe]) => ({
          nome,
          classe,
        }))}
      />
    </div>
  );
}

function Legenda({
  voci,
}: {
  voci: Array<{ nome: string; classe: string; valore?: string }>;
}) {
  return (
    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
      {voci.map((v) => (
        <span
          key={v.nome}
          className="flex items-center gap-1.5 text-[12.5px] text-ink-soft"
        >
          <span className={cx("size-2.5 rounded-full", v.classe)} />
          {v.nome}
          {v.valore ? (
            <b className="tabular-nums text-ink">{v.valore}</b>
          ) : null}
        </span>
      ))}
    </div>
  );
}

/**
 * Una ciambella con il numero in mezzo. Le fette a zero non si disegnano;
 * se è tutto a zero resta l'anello vuoto, che è la verità.
 */
export function Ciambella({
  fette,
  centro,
  dimensione = 168,
}: {
  fette: Array<{ nome: string; valore: number; classe: string }>;
  centro: { valore: string; etichetta: string };
  dimensione?: number;
}) {
  const totale = fette.reduce((s, f) => s + Math.max(0, f.valore), 0);
  const raggio = 42;
  const circonferenza = 2 * Math.PI * raggio;
  let offset = 0;

  return (
    <div
      className="relative mx-auto"
      style={{ width: dimensione, height: dimensione }}
    >
      <svg viewBox="0 0 100 100" className="size-full -rotate-90">
        <circle
          cx="50"
          cy="50"
          r={raggio}
          fill="none"
          strokeWidth="12"
          className="stroke-surface-sunken"
        />
        {totale > 0
          ? fette.map((f) => {
              if (f.valore <= 0) return null;
              const lunghezza = (f.valore / totale) * circonferenza;
              const el = (
                <circle
                  key={f.nome}
                  cx="50"
                  cy="50"
                  r={raggio}
                  fill="none"
                  strokeWidth="12"
                  strokeLinecap="butt"
                  className={f.classe}
                  strokeDasharray={`${Math.max(0, lunghezza - 1.5)} ${circonferenza}`}
                  strokeDashoffset={-offset}
                />
              );
              offset += lunghezza;
              return el;
            })
          : null}
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <p className="text-[20px] font-extrabold leading-none tabular-nums text-ink">
            {centro.valore}
          </p>
          <p className="mt-1 text-[11.5px] text-ink-soft">{centro.etichetta}</p>
        </div>
      </div>
    </div>
  );
}

/** Una riga «pallino · nome · valore» per le legende con i numeri. */
export function VoceLegenda({
  classe,
  nome,
  valore,
  quota,
}: {
  classe: string;
  nome: ReactNode;
  valore: string;
  quota?: string;
}) {
  return (
    <div className="flex items-center gap-2.5 py-1.5">
      <span className={cx("size-2.5 shrink-0 rounded-full", classe)} />
      <span className="min-w-0 flex-1 truncate text-[13.5px] text-ink">
        {nome}
      </span>
      {quota ? (
        <span className="text-[12px] tabular-nums text-ink-faint">{quota}</span>
      ) : null}
      <span className="text-[13.5px] font-bold tabular-nums text-ink">
        {valore}
      </span>
    </div>
  );
}
